// Audit-safe telemetry (SM-722): which screen, which call, how long, and
// the correlation id support needs to find the server-side log line.
//
// NEVER an amount, a user id, an email, a broker or account id, a query
// string or a request body. The event shape is a closed set of fields
// (TELEMETRY_FIELDS, pinned by test/telemetry.test.ts) and every path is
// reduced to a template before it is recorded: a segment that is not a
// known route word becomes ":id", and the query string is dropped. Events
// go to console.debug only — there is no collector to leak to.

export type TelemetryEvent =
  | {
      kind: "api";
      route: string;
      method: string;
      status: number;
      latency_ms: number;
      correlation_id?: string;
    }
  | {
      kind: "navigation";
      route: string;
      latency_ms: number;
    };

/** Every field an event may carry. Anything else is a leak. */
export const TELEMETRY_FIELDS = ["kind", "route", "method", "status", "latency_ms", "correlation_id"] as const;

// The words of every path the portal calls (api-gateway /api/auth/*,
// /api/currencies and broker-service's staff surface), and of the portal's
// own routes. A segment outside this set is data and is redacted.
const ROUTE_WORDS = new Set([
  "api",
  "auth",
  "login",
  "mfa",
  "logout",
  "me",
  "currencies",
  "broker",
  "memberships",
  "activate",
  "leave",
  "children",
  "path",
  "descendants",
  "audit",
  "staff",
  "suspend",
  "resume",
  "remove",
  "role",
  "accounts",
  "events",
  "positions",
  "orders",
  "history",
  "trades",
  "login-log",
  "order-log",
  "exposure",
  "funding",
  "pools",
  "transfers",
  "pool-transfers",
  "clearing",
  "fees",
  "schedule",
  "preview",
  "redemption-preview",
  "revenue",
  "balances",
  "earnings",
  "periods",
  "statement",
  "statement.csv",
  "payouts",
  "reconciliation",
  "overview",
  "settlement-position",
  "settlement-notes",
  "payment-requests",
  // portal routes
  "organization",
  "hierarchy",
  "users",
  "risk",
  "statements",
  "settlement",
  "invitations",
  "_app",
]);

/**
 * A path as a template: query and fragment dropped, every segment that is
 * not a route word replaced by ":id".
 *   /api/broker/brk_01/accounts/bac_9/events?limit=10 → /api/broker/:id/accounts/:id/events
 */
export function redactPath(path: string): string {
  const bare = path.split(/[?#]/, 1)[0] ?? "";
  const segments = bare.split("/").map((s) => {
    if (s === "") return s;
    return ROUTE_WORDS.has(s) ? s : ":id";
  });
  return segments.join("/") || "/";
}

type Sink = (event: TelemetryEvent) => void;

const consoleSink: Sink = (event) => {
  if (typeof console !== "undefined" && typeof console.debug === "function") {
    console.debug("[telemetry]", event);
  }
};

let sink: Sink = consoleSink;
let enabled = typeof window !== "undefined";

/** Test seam: capture events instead of printing them. */
export function setTelemetrySink(next: Sink | null, on = true): void {
  sink = next ?? consoleSink;
  enabled = on;
}

export function apiEvent(input: {
  method: string;
  path: string;
  status: number;
  latencyMs: number;
  correlationId?: string;
}): TelemetryEvent {
  const ev: TelemetryEvent = {
    kind: "api",
    route: redactPath(input.path),
    method: input.method,
    status: input.status,
    latency_ms: Math.max(0, Math.round(input.latencyMs)),
  };
  // The correlation id is an opaque server token, not user data; it is
  // what lets support find the request. Anything that is not a plain
  // token is dropped rather than recorded.
  if (input.correlationId && /^[A-Za-z0-9._:-]{1,128}$/.test(input.correlationId)) {
    ev.correlation_id = input.correlationId;
  }
  return ev;
}

export function navigationEvent(input: { route: string; latencyMs: number }): TelemetryEvent {
  return { kind: "navigation", route: redactPath(input.route), latency_ms: Math.max(0, Math.round(input.latencyMs)) };
}

export function recordApiCall(input: Parameters<typeof apiEvent>[0]): void {
  if (!enabled) return;
  sink(apiEvent(input));
}

export function recordNavigation(input: Parameters<typeof navigationEvent>[0]): void {
  if (!enabled) return;
  sink(navigationEvent(input));
}
